import { Expense } from "@/types/expense";
import { EXPENSE_TYPE_COLORS, RESPONSIVE_CONFIG } from "../constants";
import { Breakpoint } from "antd";
import { ExpenseDraft } from "../types/expenseDraft";
import dayjs from "dayjs";

interface GetOffsetProps {
  breakpoint: Partial<Record<Breakpoint, boolean>>;
  visibleFields: number;
}

interface GetLastExpenseDraftProps {
  expenseDrafts?: ExpenseDraft[];
  expense_type_id?: string;
  description_id?: string;
}

/**
 * Get the expense type color
 * @param expenseName - The expense name
 * @returns The expense type color
 */
export const getExpenseTypeColor = (expenseName?: string): string => {
  const upperCaseName = expenseName?.toUpperCase();
  return (
    EXPENSE_TYPE_COLORS[upperCaseName as keyof typeof EXPENSE_TYPE_COLORS] ||
    EXPENSE_TYPE_COLORS.DEFAULT
  );
};

/**
 * Get the offset
 * @param breakpoint - The breakpoint
 * @param visibleFields - The visible fields
 * @returns The offset
 */
export const getOffset = ({ breakpoint, visibleFields }: GetOffsetProps) => {
  const maxColumns = getMaxColumns(breakpoint);
  const rowCount = Math.ceil(visibleFields / maxColumns);
  const modFields = visibleFields % maxColumns;
  const columnsInLastRow = modFields === 0 ? maxColumns : modFields;

  if (columnsInLastRow === maxColumns) {
    return getSpan(breakpoint) * (maxColumns - 2);
  }

  if (rowCount > 1 && columnsInLastRow < maxColumns) {
    return getSpan(breakpoint) * (maxColumns - columnsInLastRow - 1);
  }

  return 0;
};

/**
 * Get the span
 * @param breakpoint - The breakpoint
 * @returns The span
 */
export const getSpan = (breakpoint: Partial<Record<Breakpoint, boolean>>) => {
  if (breakpoint.lg) {
    return RESPONSIVE_CONFIG.lg.span;
  }
  if (breakpoint.md) {
    return RESPONSIVE_CONFIG.md.span;
  }
  if (breakpoint.sm) {
    return RESPONSIVE_CONFIG.sm.span;
  }
  if (breakpoint.xs) {
    return RESPONSIVE_CONFIG.xs.span;
  }

  return RESPONSIVE_CONFIG.xs.span;
};

/**
 * Get the col props
 * @param visibleFields - The visible fields
 * @returns The col props
 */
export const getColProps = (visibleFields: number) => ({
  xs: RESPONSIVE_CONFIG.xs.span,
  sm: RESPONSIVE_CONFIG.sm.span,
  md: visibleFields + 1 > 2 ? RESPONSIVE_CONFIG.md.span : undefined,
  lg: visibleFields + 1 > 3 ? RESPONSIVE_CONFIG.lg.span : undefined,
});

/**
 * Get the max columns
 * @param breakpoint - The breakpoint
 * @returns The max columns
 */
export const getMaxColumns = (
  breakpoint: Partial<Record<Breakpoint, boolean>>
) => {
  if (breakpoint.lg) {
    return RESPONSIVE_CONFIG.lg.maxColumns;
  }
  if (breakpoint.md) {
    return RESPONSIVE_CONFIG.md.maxColumns;
  }
  if (breakpoint.sm) {
    return RESPONSIVE_CONFIG.sm.maxColumns;
  }
  if (breakpoint.xs) {
    return RESPONSIVE_CONFIG.xs.maxColumns;
  }

  return RESPONSIVE_CONFIG.xs.maxColumns;
};

/**
 * Get the is last row full
 * @param breakpoint - The breakpoint
 * @param visibleFields - The visible fields
 * @returns The is last row full
 */
export const getIsLastRowFull = (
  breakpoint: Partial<Record<Breakpoint, boolean>>,
  visibleFields: number
) => {
  const maxColumns = getMaxColumns(breakpoint);
  return visibleFields % maxColumns === 0;
};

/**
 * Get the required fields from the expenses
 * @param expenses - The expenses
 * @returns The expense required fields
 */
export const getExpenseRequiredFields = (
  expenses: Expense[] | ExpenseDraft[]
) => {
  const defaultKeys = {
    date: false,
    description: false,
    price: false,
    quantity: false,
    unit: false,
    supplier: false,
  };

  // If no expenses, return all false
  if (expenses.length === 0) {
    return defaultKeys;
  }

  const keys = defaultKeys;
  expenses.forEach((expense) => {
    if (expense.expense_type?.required_fields) {
      expense.expense_type.required_fields.forEach((field) => {
        if (keys[field as keyof typeof keys] === false) {
          keys[field as keyof typeof keys] = true;
        }
      });
    }
  });

  return keys;
};

/**
 * Get the last expense date from the expense drafts
 * @param expenseDrafts - The expense drafts
 * @returns The last expense date
 */
export const getLastExpenseDraftDate = (expenseDrafts: ExpenseDraft[]) => {
  if (expenseDrafts.length === 0) {
    return;
  }

  return dayjs(expenseDrafts[0].date);
};

/**
 * Get the last expense from the expense draft based on description (prioritize history than drafts)
 * @param expenseDrafts - The expense draft
 * @param expense_type_id - The expense type id
 * @param description_id - The description id
 * @returns The last expense draft
 */
export const getLastExpenseDraft = ({
  expenseDrafts,
  expense_type_id,
  description_id,
}: GetLastExpenseDraftProps) => {
  if (
    !expense_type_id ||
    !description_id ||
    !expenseDrafts ||
    expenseDrafts.length === 0
  ) {
    return;
  }

  const expenseDraft = expenseDrafts.find(
    (expenseDraft) =>
      expenseDraft.expense_type_id === expense_type_id &&
      expenseDraft.description_id === description_id
  );

  return expenseDraft;
};
